import React, { useEffect } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useNavigate, useParams } from "react-router-dom";
import { PageBreadcrumbs } from "../../../components/common/PageBreadcrumbs";
import { NavigationBlocker } from "../../../components/common/NavigationBlocker";
import { useShopifySaveBar } from "../../../hooks/useShopifySaveBar";
import {
  useDownsellEditorStore,
  selectDownsellIsDirty,
} from "../../../store/useDownsellEditorStore";
import { useOffersListStore } from "../../../store/useOffersListStore";
import { SalesBoosterTabBar } from "../SalesBoosterTabBar";
import { ConfigSection, NameSection } from "./DownsellEditorConfig";
import { DesignSection } from "./DownsellEditorDesign";
import { DownsellEditorPreview } from "./DownsellEditorPreview";

// ─── Page ─────────────────────────────────────────────────────────────────────

const DownsellEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();

  const config = useDownsellEditorStore((s) => s.config);
  const offerId = useDownsellEditorStore((s) => s.offerId);
  const isLoading = useDownsellEditorStore((s) => s.isLoading);
  const isSaving = useDownsellEditorStore((s) => s.isSaving);
  const isDirty = useDownsellEditorStore(selectDownsellIsDirty);

  const updateField = useDownsellEditorStore((s) => s.updateField);
  const initNew = useDownsellEditorStore((s) => s.initNew);
  const loadFromBackend = useDownsellEditorStore((s) => s.loadFromBackend);
  const save = useDownsellEditorStore((s) => s.save);
  const discardChanges = useDownsellEditorStore((s) => s.discardChanges);
  const reset = useDownsellEditorStore((s) => s.reset);

  const refetchOffers = useOffersListStore((s) => s.refetch);

  useEffect(() => {
    if (routeId) {
      if (offerId !== routeId) {
        loadFromBackend(routeId).catch(() => {});
      }
      return;
    }
    if (offerId === null) {
      initNew();
    }
  }, [routeId, offerId, loadFromBackend, initNew]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  useShopifySaveBar({
    isDirty,
    isSaving,
    onSave: async () => {
      const wasNew = offerId === null;
      const savedId = await save();
      refetchOffers().catch(() => {});
      if (wasNew) {
        navigate(`/sales-booster/downsells/${savedId}`, { replace: true });
      }
    },
    onDiscard: discardChanges,
  });

  return (
    <div>
      <NavigationBlocker isDirty={isDirty} />

      <s-page heading={config.internalName || "Nuevo downsell"}>
        <PageBreadcrumbs
          items={[
            { label: "Impulsor de ventas", to: "/sales-booster" },
            { label: "Downsells", to: "/sales-booster/downsells" },
          ]}
        />
        <s-stack direction="block" gap="large-200">
          <SalesBoosterTabBar />

          {/* Header (active toggle + status) */}
          <div className="flex items-center justify-end py-1">
            <div className="flex items-center gap-3">
              <s-switch
                label="Activar downsell"
                labelAccessibilityVisibility="exclusive"
                checked={config.isActive}
                onChange={(e: CallbackEvent<"s-switch">) =>
                  updateField("isActive", e.currentTarget.checked)
                }
              />
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide border ${
                  config.isActive
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-gray-100 text-gray-500 border-gray-200"
                }`}
              >
                {config.isActive ? "Activo" : "Inactivo"}
              </span>
            </div>
          </div>

          {isLoading && (
            <s-banner tone="info">
              Cargando configuración del downsell…
            </s-banner>
          )}

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            {/* Left: settings */}
            <s-stack direction="block" gap="base">
              <NameSection
                value={config.internalName}
                onChange={(v) => updateField("internalName", v)}
              />
              <ConfigSection
                values={{
                  triggerType: config.triggerType,
                  triggerProducts: config.triggerProducts,
                  timesShownLimit: config.timesShownLimit,
                  backgroundImagePreset: config.backgroundImagePreset,
                  backgroundColor: config.backgroundColor,
                  borderRadius: config.borderRadius,
                  borderWidth: config.borderWidth,
                  borderColor: config.borderColor,
                  pricingAction: config.pricingAction,
                  pricingValue: config.pricingValue,
                }}
                onConfigChange={updateField}
              />
              <DesignSection
                values={{
                  titleTemplate: config.titleTemplate,
                  titleColor: config.titleColor,
                  titleSize: config.titleSize,
                  subtitleTemplate: config.subtitleTemplate,
                  subtitleColor: config.subtitleColor,
                  subtitleSize: config.subtitleSize,
                  discountBadgeText: config.discountBadgeText,
                  discountBadgeTextColor: config.discountBadgeTextColor,
                  discountBadgeDiscountColor: config.discountBadgeDiscountColor,
                  discountBadgeSize: config.discountBadgeSize,
                  ctaAcceptStyle: config.ctaAcceptStyle,
                  ctaDeclineStyle: config.ctaDeclineStyle,
                }}
                onDesignChange={updateField}
              />
            </s-stack>

            {/* Right: preview */}
            <DownsellEditorPreview
              backgroundImagePreset={config.backgroundImagePreset}
              backgroundColor={config.backgroundColor}
              borderRadius={config.borderRadius}
              borderWidth={config.borderWidth}
              borderColor={config.borderColor}
              titleTemplate={config.titleTemplate}
              titleColor={config.titleColor}
              titleSize={config.titleSize}
              subtitleTemplate={config.subtitleTemplate}
              subtitleColor={config.subtitleColor}
              subtitleSize={config.subtitleSize}
              discountBadgeText={config.discountBadgeText}
              discountBadgeTextColor={config.discountBadgeTextColor}
              discountBadgeDiscountColor={config.discountBadgeDiscountColor}
              discountBadgeSize={config.discountBadgeSize}
              pricingAction={config.pricingAction}
              pricingValue={config.pricingValue}
              ctaAcceptStyle={config.ctaAcceptStyle}
              ctaDeclineStyle={config.ctaDeclineStyle}
            />
          </div>
        </s-stack>
      </s-page>
    </div>
  );
};

export default DownsellEditor;
